
import { Vue, Component, Ref, Watch } from 'vue-property-decorator';
import RegisterDeliveryModal from './components/RegisterDeliveryModal.vue';
import SalesOrderDetailModal from './components/SalesOrderDetailModal.vue';
import lazyInject from '@/di';
import SupplyService from '@/services/supply.service';
import moment from 'moment';
import { AuthEnum } from '@/services/permission.service';

@Component({
  components: {
    RegisterDeliveryModal,
    SalesOrderDetailModal,
  },
})
export default class SalesOrder extends Vue {
  loading = false;

  AuthEnum = AuthEnum;

  @Ref('registerDeliveryModal')
  registerDeliveryModal!: RegisterDeliveryModal;

  @Ref('salesOrderDetailModal')
  salesOrderDetailModal!: SalesOrderDetailModal;

  @lazyInject(SupplyService)
  supplyService!: SupplyService;

  dataSource: any[] = [];

  filterForm: any = {
    order_no: '',
    tracking_number: '',
    // 采购日期（下单时间）默认近一周（含今天共 7 天）
    start_date: moment().subtract(6, 'days').format('YYYY-MM-DD'),
    end_date: moment().format('YYYY-MM-DD'),
    // 预计到货
    arrival_date_start: '',
    arrival_date_end: '',
    // 发运
    shipment_date_start: '',
    shipment_date_end: '',
    // 签收（实际到货）
    sign_time_start: '',
    sign_time_end: '',
    status: undefined,
  };

  arrivalStatusMap: any = {
    2: '待发货',
    3: '已发货',
    4: '已完成',
  };

  columns = [
    {
      title: '订单编号',
      dataIndex: 'purchase_order_no',
      align: 'center',
      width: 140,
    },
    {
      title: '发货城市',
      dataIndex: 'ship_area',
      align: 'center',
      width: 100,
    },
    {
      title: '箱数',
      dataIndex: 'box_count',
      align: 'center',
      width: 80,
    },
    {
      title: '壶数',
      dataIndex: 'pot_count',
      align: 'center',
      width: 100,
    },
    {
      title: '采购日期',
      dataIndex: 'purchase_date',
      align: 'center',
      width: 180,
    },
    {
      title: '发货日期',
      dataIndex: 'ship_date',
      align: 'center',
      width: 180,
    },
    {
      title: '发货单号',
      dataIndex: 'tracking_number',
      align: 'center',
      width: 180,
    },
    {
      title: '到货状态',
      dataIndex: 'status_text',
      align: 'center',
      width: 140,
      scopedSlots: { customRender: 'arrivalStatus' },
    },
    {
      title: '预计到货',
      dataIndex: 'expect_arrive',
      align: 'center',
      width: 180,
    },
    {
      title: '实际到货',
      dataIndex: 'actual_arrive',
      align: 'center',
      width: 180,
    },
    {
      title: '操作',
      dataIndex: 'action',
      align: 'center',
      fixed: 'right',
      width: 120,
      scopedSlots: { customRender: 'action' },
    },
  ];

  pagination: any = {
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: this.$store.state.pageSize.pageSize,
    showTotal: () => `共${this.pagination.total}条`,
    onChange: this.paginationChange,
    onShowSizeChange: this.onShowSizeChange,
  };

  @Watch('$route')
  async handleSearch() {
    const {
      order_no,
      tracking_number,
      start_date,
      end_date,
      status,
      arrival_date_start,
      arrival_date_end,
      shipment_date_start,
      shipment_date_end,
      sign_time_start,
      sign_time_end,
      page,
      pageSize,
    } = this.$route.query;
    const params: any = { page: page || 1, pageSize: pageSize || 10 };
    if (order_no) {
      params.purchase_order_no = order_no;
      this.filterForm.order_no = order_no;
    }
    if (tracking_number) {
      params.tracking_number = tracking_number;
      this.filterForm.tracking_number = tracking_number;
    }
    if (start_date && end_date) {
      params.start_date = start_date;
      params.end_date = end_date;
      this.filterForm.start_date = start_date;
      this.filterForm.end_date = end_date;
    }
    if (arrival_date_start && arrival_date_end) {
      params.arrival_date_start = arrival_date_start;
      params.arrival_date_end = arrival_date_end;
      this.filterForm.arrival_date_start = arrival_date_start;
      this.filterForm.arrival_date_end = arrival_date_end;
    }
    if (shipment_date_start && shipment_date_end) {
      params.shipment_date_start = shipment_date_start;
      params.shipment_date_end = shipment_date_end;
      this.filterForm.shipment_date_start = shipment_date_start;
      this.filterForm.shipment_date_end = shipment_date_end;
    }
    if (sign_time_start && sign_time_end) {
      params.sign_time_start = sign_time_start;
      params.sign_time_end = sign_time_end;
      this.filterForm.sign_time_start = sign_time_start;
      this.filterForm.sign_time_end = sign_time_end;
    }
    if (status !== undefined && status !== null) {
      params.status = status;
      this.filterForm.status = +status;
    }
    this.getList(params);
  }

  mounted() {
    this.handleSearch();
  }

  getQueryParams() {
    const {
      order_no,
      tracking_number,
      start_date,
      end_date,
      status,
      arrival_date_start,
      arrival_date_end,
      shipment_date_start,
      shipment_date_end,
      sign_time_start,
      sign_time_end,
    } = this.filterForm;
    const { current, pageSize } = this.pagination;
    const query: any = { page: current, pageSize };
    if (order_no) {
      query.order_no = order_no;
    }
    if (tracking_number) {
      query.tracking_number = tracking_number;
    }
    if (start_date && end_date) {
      query.start_date = start_date;
      query.end_date = end_date;
    }
    if (arrival_date_start && arrival_date_end) {
      query.arrival_date_start = arrival_date_start;
      query.arrival_date_end = arrival_date_end;
    }
    if (shipment_date_start && shipment_date_end) {
      query.shipment_date_start = shipment_date_start;
      query.shipment_date_end = shipment_date_end;
    }
    if (sign_time_start && sign_time_end) {
      query.sign_time_start = sign_time_start;
      query.sign_time_end = sign_time_end;
    }
    if (status !== undefined && status !== null) {
      query.status = status;
    }
    query.timestamp = Math.round(Date.now() / 1000);
    this.$router.push({
      query,
    });
  }

  search() {
    this.pagination.current = 1;
    this.getQueryParams();
  }

  async getList(params: any) {
    // TODO: 调用供应商销售单列表接口，参数：this.pagination.current
    const res = await this.supplyService.getPlatOrderList(params, this.$el);
    this.loading = false;
    this.dataSource = res.list;
    this.pagination.total = res.total;
    this.pagination.current = res.page;
    this.pagination.pageSize = res.pageSize;
  }

  reset() {
    this.filterForm = {
      order_no: '',
      tracking_number: '',
      start_date: moment().subtract(6, 'days').format('YYYY-MM-DD'),
      end_date: moment().format('YYYY-MM-DD'),
      arrival_date_start: '',
      arrival_date_end: '',
      shipment_date_start: '',
      shipment_date_end: '',
      sign_time_start: '',
      sign_time_end: '',
      status: undefined,
    };
    this.pagination.current = 1;
    this.getQueryParams();
  }

  formatDate(date: any) {
    return date ? moment(date).format('YYYY-MM-DD') : '';
  }

  onChangePurchaseDate(dates: any) {
    this.filterForm.start_date = this.formatDate(dates && dates[0]);
    this.filterForm.end_date = this.formatDate(dates && dates[1]);
  }

  onChangeShipmentDate(dates: any) {
    this.filterForm.shipment_date_start = this.formatDate(dates && dates[0]);
    this.filterForm.shipment_date_end = this.formatDate(dates && dates[1]);
  }

  onChangeArrivalDate(dates: any) {
    this.filterForm.arrival_date_start = this.formatDate(dates && dates[0]);
    this.filterForm.arrival_date_end = this.formatDate(dates && dates[1]);
  }

  onChangeSignTime(dates: any) {
    this.filterForm.sign_time_start = this.formatDate(dates && dates[0]);
    this.filterForm.sign_time_end = this.formatDate(dates && dates[1]);
  }

  paginationChange(page: number, pageSize: number) {
    this.pagination.current = page;
    this.pagination.pageSize = pageSize;
    this.getQueryParams();
  }

  onShowSizeChange(current: number, size: number) {
    this.pagination.current = 1;
    this.pagination.pageSize = size;
    this.getQueryParams();
  }

  viewDelivery(record: any) {
    // TODO: 查看发货单
    console.log(record);
  }

  registerDelivery(record: any) {
    this.registerDeliveryModal.open(record);
  }

  viewDetail(record: any) {
    this.salesOrderDetailModal.open(record);
  }
}
